import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CaseFrameLabels } from "@/zapi-frontend/pages/CreateProjectPage/ui/blocks/CaseFrame";
import type { ProjectDetailsLabels } from "@/zapi-frontend/pages/CreateProjectPage/ui/blocks/ProjectDetails";
import type { SetupLabels } from "@/zapi-frontend/widgets/CreateProjectChat/ui/blocks/ProjectSetup";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

// The design system's blocks come with English text; the create page hands them the interface language.
export function useNewProjectLabels() {
  const { t, i18n } = useTranslation("createProject");

  return useMemo(() => {
    const setup: Partial<SetupLabels> = {
      launchAnalysis: t("newProject.launch"),
      launching: t("newProject.launching"),
      launchAnalysisBlocked: (reason) => `${t("newProject.launch")}: ${reason}`,
      launchReadyTitle: t("newProject.launchReadyTitle"),
      launchReadyDescription: t("newProject.launchReadyDescription"),
      launchUnfinishedTitle: t("newProject.launchEarlyTitle"),
      launchUnfinishedDescription: t("newProject.launchEarlyDescription"),
      reviewFrame: t("newProject.reviewFrame"),
      keepGoing: t("newProject.keepGoing"),
      launchAnyway: t("newProject.launchAnyway"),
      cancel: t("newProject.cancel"),
      linkDialogTitle: t("newProject.linkTitle"),
      linkLabel: t("newProject.linkLabel"),
      linkPlaceholder: t("newProject.linkPlaceholder"),
      invalidLink: t("newProject.linkInvalid"),
      addLink: t("newProject.addLink"),
      addFiles: t("newProject.addFiles"),
      addWebLink: t("newProject.addWebLink"),
      read: t("newProject.sourceRead"),
      reading: t("newProject.sourceReading"),
      remove: t("newProject.remove"),
      removeSource: (name) => t("newProject.removeSource", { name }),
      vault: t("newProject.vault"),
      dropFilesTitle: t("newProject.dropFiles"),
      dropFilesDescription: t("newProject.dropHint"),
      close: t("newProject.close")
    };

    const projectDetails: Partial<ProjectDetailsLabels> = {
      projectName: t("newProject.projectName"),
      untitledProject: t("newProject.untitled"),
      description: t("newProject.description"),
      descriptionPlaceholder: t("newProject.descriptionPlaceholder"),
      language: t("newProject.language"),
      languagePlaceholder: t("newProject.languagePlaceholder"),
      notSet: t("newProject.notSet"),
      version: t("newProject.version"),
      visibility: t("newProject.visibility"),
      visibilityOrganization: t("newProject.organization"),
      visibilityPrivate: t("newProject.private"),
      uploadPicture: t("newProject.uploadPicture"),
      shufflePicture: t("newProject.shufflePicture"),
      removePicture: t("newProject.removePicture"),
      showProjectDetails: t("newProject.showDetails"),
      addTag: t("newProject.tags.addTag"),
      tagWithValue: (name, value) => t("newProject.tags.tagWithValue", { name, value }),
      removeTagFromProject: (name) => t("newProject.tags.removeTagFromProject", { tag: name }),
      removeFromProject: t("newProject.tags.removeFromProject"),
      allTagsOnProject: t("newProject.tags.allTagsOnProject"),
      noTagsYet: t("newProject.tags.noTagsYet"),
      manageTags: t("newProject.tags.manageTags"),
      backToTags: t("newProject.tags.backToTags"),
      edit: t("newProject.tags.edit"),
      optionsOf: (name) => t("newProject.tags.optionsOf", { name }),
      current: t("newProject.tags.current"),
      noOptions: t("newProject.tags.noOptions"),
      noOptionsAddWithEdit: t("newProject.tags.noOptionsAddWithEdit"),
      setTagValue: (name) => t("newProject.tags.setTagValue", { name }),
      typeTagValue: (name) => t("newProject.tags.typeTagValue", { name: name.toLowerCase() }),
      pressEnter: t("newProject.tags.pressEnter"),
      manageTagsTitle: t("newProject.tags.manageTagsTitle"),
      tagChangesDescription: t("newProject.tags.tagChangesDescription"),
      createNewTag: t("newProject.tags.createNewTag"),
      done: t("newProject.tags.done"),
      close: t("newProject.close"),
      deleteTagForEveryone: (name) => t("newProject.tags.deleteTagForEveryone", { name }),
      deleteForEveryone: t("newProject.tags.deleteForEveryone"),
      deleteTagTitle: (name) => t("newProject.tags.deleteTagTitle", { name }),
      deleteTagDescription: (onThisProject) =>
        onThisProject ? t("newProject.tags.deleteTagDescriptionHere") : t("newProject.tags.deleteTagDescription"),
      editTagTitle: (name) => t("newProject.tags.editTagTitle", { name }),
      newTagTitle: t("newProject.tags.newTagTitle"),
      newTagDescription: t("newProject.tags.newTagDescription"),
      tagName: t("newProject.tags.tagName"),
      tagNamePlaceholder: t("newProject.tags.tagNamePlaceholder"),
      tagNameTaken: (name) => t("newProject.tags.tagNameTaken", { name }),
      tagType: t("newProject.tags.tagType"),
      tagTypeGroup: t("newProject.tags.tagTypeGroup"),
      tagTypeOptions: t("newProject.tags.tagTypeOptions"),
      tagTypeText: t("newProject.tags.tagTypeText"),
      tagTypeOptionsHint: t("newProject.tags.tagTypeOptionsHint"),
      tagTypeTextHint: t("newProject.tags.tagTypeTextHint"),
      options: t("newProject.tags.options"),
      removeOption: (option) => t("newProject.tags.removeOption", { option }),
      addOption: t("newProject.tags.addOption"),
      newOption: t("newProject.tags.newOption"),
      addAnotherOption: t("newProject.tags.addAnotherOption"),
      typeAnOption: t("newProject.tags.typeAnOption"),
      removedOptionWarning: t("newProject.tags.removedOptionWarning"),
      anyText: t("newProject.tags.anyText"),
      noOptionsSummary: t("newProject.tags.noOptionsSummary"),
      cancel: t("newProject.cancel"),
      delete: t("newProject.tags.delete"),
      saveChanges: t("newProject.saveChanges"),
      createTag: t("newProject.tags.createTag")
    };

    const caseFrame: Partial<CaseFrameLabels> = {
      activeSection: t("newProject.sectionActive"),
      settledSection: t("newProject.sectionSettled"),
      openCount: (count) => t("newProject.sectionOpenCount", { count }),
      completeness: t("newProject.completeness"),
      sectionsSettled: (settled, total) => t("newProject.sectionsSettled", { settled, total }),
      percentComplete: (percent) => t("newProject.completenessValue", { percent })
    };

    return {
      title: t("newProject.title"),
      untitled: t("newProject.untitled"),
      vault: t("newProject.vault"),
      caseFrameTitle: t("newProject.caseFrame"),
      sectionPlaceholder: t("newProject.sectionPlaceholder"),
      launchBlockedDecision: t("newProject.launchBlockedDecision"),
      launchBlockedName: t("newProject.launchBlockedName"),
      organization: t("newProject.organization"),
      private: t("newProject.private"),
      versionInitial: displayVersionName("Initial"),
      setup,
      projectDetails,
      caseFrame
    };
    // i18n.language re-runs the labels when the interface language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);
}
